import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Badge, FormControl, OverlayTrigger, Table, Tooltip, Pagination } from 'react-bootstrap';
import { search } from '../helpers/search';

function removeSpecialChars(str) {
  try {
    str = str.toString();
  } catch (e) {
    return '';
  }
  return str.replace(/[^a-z0-9]/gi, '');
}

const getValue = (admin, header) => {
  if (header.render) {
    return header.render(admin);
  }

  let value = admin[header.name];
  if (header.type !== 'boolean' && !value) return '-';

  if (Array.isArray(value)) {
    value = value.join(', ');
  }

  let returnElement = typeof value === 'object' ? value[header.objectKey] : value;

  switch (header.type) {
    case 'email':
      returnElement = (
        <a className="text-dark" onClick={e => e.stopPropagation()} href={`mailto:${value}`}>
          {value}
        </a>
      );

      break;
    case 'phone':
      returnElement = (
        <a className="text-dark" onClick={e => e.stopPropagation()} href={`tel:${value}`}>
          {value}
        </a>
      );
      break;
    case 'date':
      returnElement = moment(value).format('MMM DD YYYY');
      break;
    case 'boolean':
      returnElement = returnElement ? 'Yes' : 'No';
      break;
    case 'number':
      returnElement = returnElement.toFixed(2);
  }

  if (header.showTooltip) {
    returnElement = (
      <OverlayTrigger
        key={header.name}
        placement="bottom"
        delay={50}
        overlay={
          <Tooltip className="custom-tooltip" id={header.name} style={{ minWidth: 400 }}>
            <>{returnElement}</>
          </Tooltip>
        }
      >
        <div
          style={{
            cursor: 'pointer',
            maxWidth: '250px',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            display: '-webkit-box',
            overflow: 'hidden'
          }}
        >
          {returnElement}
        </div>
      </OverlayTrigger>
    );
  }

  if (header.showAsBadge) {
    returnElement = (
      <Badge className="p-1 px-2" style={{ fontSize: 12 }} variant={header.badgeVariant || 'primary'}>
        {returnElement}
      </Badge>
    );
  }

  if (header.linkToKey) {
    let url = admin[header.linkToKey] || '';
    if (!url.startsWith('https://') && !url.startsWith('http://')) {
      url = 'https://' + url;
    }

    returnElement = (
      <a className="text-dark" target="_blank" href={url}>
        {header.urlText || returnElement}
      </a>
    );
  }

  return returnElement;
};

const DataTable = ({
  headers = [],
  data = [],
  rowKeyField,
  searchQueryKeys = [],
  actionCell,
  additionalComponents,
  searchHint = 'Quick Search...',
  onSearch,
  useInternalSearch = true,
  onBottomReached,
  loadingMoreData = false,
  pagination = null,
  onPageChange,
  loadingData = false,
  fixedLayout = false
}) => {
  const [filteredData, setFilteredData] = useState(data);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!useInternalSearch) {
      setFilteredData(data);
      return;
    }

    const debounceTimeout = setTimeout(() => {
      setFilteredData(search(query, data, searchQueryKeys));
    }, 300); // Adjust the delay (in milliseconds) as needed

    return () => clearTimeout(debounceTimeout); // Cleanup the timeout on unmount or when query changes
  }, [data, query, useInternalSearch]);

  useEffect(() => {
    if (useInternalSearch || !onSearch) return;

    const debounceTimeout = setTimeout(() => {
      onSearch(query);
    }, 800); // Adjust the delay (in milliseconds) as needed

    return () => clearTimeout(debounceTimeout); // Cleanup the timeout on unmount or when query changes
  }, [query, useInternalSearch]);

  // Handle infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (onBottomReached && !loadingMoreData) {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        if (scrollTop + windowHeight >= documentHeight - 100) {
          onBottomReached();
        }
      }
    };

    if (onBottomReached) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [onBottomReached, loadingMoreData]);

  return (
    <div
      className="d-flex flex-column h-100"
      style={{ minHeight: 0, minWidth: 0, overflow: 'hidden' }}
    >
      <div className="d-flex mb-4 w-100" style={{ flexShrink: 0 }}>
        <div className="flex-grow-1">
          <FormControl
            size="sm"
            placeholder={searchHint}
            className="entity-table-input-field"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        {additionalComponents && <div className="ml-2">{additionalComponents()}</div>}
      </div>

      <div
        className="table-responsive border p-0"
        style={{ borderRadius: '10px', minWidth: 0, maxWidth: '100%' }}
      >
        <Table hover className="m-0" style={{ tableLayout: fixedLayout ? 'fixed' : 'auto' }}>
          {/* z-index stays low so page-level overlays (react-select, etc.) are not covered */}
          <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }} >
            <tr style={{ borderBottom: '1px solid #dee2e6' }}>
              {headers.map(({ name, minWidth, width }) => (
                <th
                  key={name}
                  style={{
                    minWidth: minWidth || '200px',
                    width: fixedLayout ? width || minWidth || '200px' : width,
                    padding: '14px 16px',
                    textAlign: 'left',
                    color: '#6c757d',
                    backgroundColor: '#fff'
                  }}
                >
                  {name}
                </th>
              ))}
              {actionCell && <th style={{ minWidth: 70, padding: '10px', color: '#6c757d', backgroundColor: '#fff' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loadingData ? (
              <tr>
                <td colSpan={headers.length} className="py-3">
                  <div className="px-3">
                    <div className="shimmer shimmer-line" style={{ marginBottom: 8, width: '40%' }} />
                    <div className="shimmer shimmer-line" style={{ marginBottom: 8, width: '30%' }} />
                    <div className="shimmer shimmer-line" style={{ width: '25%' }} />
                  </div>
                </td>
              </tr>
            ) : filteredData.length > 0 ? (
              <>
                {filteredData.map(dataObj => (
                  <tr key={dataObj[rowKeyField]} style={{ borderBottom: '1px solid #f1f3f4' }}>
                    {headers.map(header => (
                      <td
                        key={header.name}
                        style={{
                          width: fixedLayout ? header.width || header.minWidth || '200px' : header.width,
                          padding: '12px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          verticalAlign: 'middle',
                          ...(header.cellStyle || {})
                        }}
                      >
                        {getValue(dataObj, header)}
                      </td>
                    ))}
                    {actionCell && <td style={{ padding: '12px 16px' }}>{actionCell(dataObj)}</td>}
                  </tr>
                ))}
                {loadingMoreData && (
                  <tr>
                    <td colSpan={actionCell ? headers.length + 1 : headers.length} style={{ padding: '12px 16px' }}>
                      <h6 className="text-muted text-center mb-0">Loading more...</h6>
                    </td>
                  </tr>
                )}
              </>
            ) : (
              <tr>
                <td colSpan={actionCell ? headers.length + 1 : headers.length} style={{ padding: '12px 16px' }}>
                  <h6 className="text-muted text-center mb-0">Nothing to show</h6>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination && onPageChange && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.First onClick={() => onPageChange(1)} disabled={pagination.pageNumber <= 1}>
              <span className="text-dark">&lt;&lt;</span>
            </Pagination.First>
            <Pagination.Prev
              linkClassName="text-dark"
              onClick={() => onPageChange(pagination.pageNumber - 1)}
              disabled={pagination.pageNumber <= 1}
            >
              <span className="text-dark">&lt;</span>
            </Pagination.Prev>

            {/* Show page numbers */}
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              let pageNum;
              if (pagination.totalPages <= 5) {
                pageNum = i + 1;
              } else if (pagination.pageNumber <= 3) {
                pageNum = i + 1;
              } else if (pagination.pageNumber >= pagination.totalPages - 2) {
                pageNum = pagination.totalPages - 4 + i;
              } else {
                pageNum = pagination.pageNumber - 2 + i;
              }

              return (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === pagination.pageNumber}
                  onClick={() => onPageChange(pageNum)}
                >
                  <span className="text-dark">{pageNum}</span>
                </Pagination.Item>
              );
            })}

            <Pagination.Next
              onClick={() => onPageChange(pagination.pageNumber + 1)}
              disabled={pagination.pageNumber >= pagination.totalPages}
            >
              <span className="text-dark">&gt;</span>
            </Pagination.Next>
            <Pagination.Last
              onClick={() => onPageChange(pagination.totalPages)}
              disabled={pagination.pageNumber >= pagination.totalPages}
            >
              <span className="text-dark">&gt;&gt;</span>
            </Pagination.Last>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default DataTable;
